import styled, { createGlobalStyle } from 'styled-components';

/**
 * PGIM PMA Workbench / Aladdin / FactSet — institutional-finance design tokens.
 * Deliberately divergent from the rest of the OMS shell (dark-navy + glass-card).
 * Reference: feedback_institutional_ux_standard memory.
 */
/**
 * Tokens are emitted as CSS custom properties with hex fallbacks so that:
 *  - Standalone /portco/new and /portco/covenants/heatmap routes render with
 *    the original PGIM/Aladdin light-institutional palette (the fallback wins
 *    because no CSS var is defined at the document root).
 *  - When the workbench wraps embedded screens in `.portco-embedded`, the dark
 *    overrides defined in `PortcoEmbeddedDark` win, retinting backgrounds,
 *    text, and borders to match the AxiomFlow dark Alternatives shell.
 *
 * Status colors stay literal — semantic red/amber/green don't flip in dark mode.
 */
export const portcoTokens = {
  // Surfaces
  surfaceHeader: 'var(--portco-surface-header, #0b1e3a)',
  surfaceBody: 'var(--portco-surface-body, #ffffff)',
  surfaceMuted: 'var(--portco-surface-muted, #f7f8fa)',
  surfaceTile: 'var(--portco-surface-tile, #ffffff)',

  // Ink
  inkPrimary: 'var(--portco-ink-primary, #0b1e3a)',
  inkBody: 'var(--portco-ink-body, #1f2937)',
  inkMuted: 'var(--portco-ink-muted, #4b5563)',
  inkSubtle: 'var(--portco-ink-subtle, #6b7280)',
  inkOnDark: 'var(--portco-ink-on-dark, #ffffff)',
  inkOnDarkMuted: 'var(--portco-ink-on-dark-muted, #aebbd0)',

  // Lines
  hairline: 'var(--portco-hairline, #e5e7eb)',
  hairlineStrong: 'var(--portco-hairline-strong, #d1d5db)',
  divider: 'var(--portco-divider, #eef0f3)',

  // Accents — single accent, no rainbow
  accent: 'var(--portco-accent, #0b1e3a)',
  accentHover: 'var(--portco-accent-hover, #13294e)',
  accentSubtle: 'var(--portco-accent-subtle, #eaf0fa)',

  // Status (covenants / data quality) — literal, semantic
  statusBreached: '#a3160b',
  statusBreachedSubtle: '#fdecea',
  statusWarning: '#a96a00',
  statusWarningSubtle: '#fff4e0',
  statusPassing: '#1f6f3f',
  statusPassingSubtle: '#e7f4ec',
  statusNoData: '#6b7280',
  statusNoDataSubtle: '#f1f3f5',

  // Type
  fontSans: "'Inter', 'Helvetica Neue', system-ui, -apple-system, sans-serif",
  fontMono: "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

/**
 * Reset OMS shell's dark-glass default for portco routes only.
 * Scoped via .portco-surface wrapper on every screen.
 */
export const PortcoGlobal = createGlobalStyle`
  .portco-surface {
    background: ${portcoTokens.surfaceBody};
    color: ${portcoTokens.inkBody};
    font-family: ${portcoTokens.fontSans};
    min-height: 100vh;
  }
  .portco-surface * {
    box-sizing: border-box;
  }
  .portco-surface a {
    color: ${portcoTokens.accent};
    text-decoration: none;
  }
  .portco-surface a:hover {
    color: ${portcoTokens.accentHover};
    text-decoration: underline;
  }
`;

/**
 * Dark-mode override for portco screens hosted inside the AxiomFlow Alternatives
 * shell (Portfolio Companies workbench). Wrap embedded screens in
 * `<div className="portco-embedded">` and these CSS vars retint surfaces, text,
 * and hairlines to match the dark institutional chrome.
 *
 * Standalone /portco/new and /portco/covenants/heatmap routes are unaffected
 * because they don't carry the `.portco-embedded` class — the var() fallbacks
 * in `portcoTokens` keep the original light palette.
 */
export const PortcoEmbeddedDark = createGlobalStyle`
  .portco-embedded {
    /* Surfaces — transparent body lets the dark Alternatives shell show through */
    --portco-surface-header: rgba(15, 23, 42, 0.55);
    --portco-surface-body: transparent;
    --portco-surface-muted: rgba(255, 255, 255, 0.03);
    --portco-surface-tile: rgba(255, 255, 255, 0.025);

    /* Ink — slate scale for body, near-white for headlines */
    --portco-ink-primary: #e2e8f0;
    --portco-ink-body: #cbd5e1;
    --portco-ink-muted: #94a3b8;
    --portco-ink-subtle: #64748b;
    --portco-ink-on-dark: #e2e8f0;
    --portco-ink-on-dark-muted: #94a3b8;

    /* Lines — translucent whites */
    --portco-hairline: rgba(255, 255, 255, 0.08);
    --portco-hairline-strong: rgba(255, 255, 255, 0.18);
    --portco-divider: rgba(255, 255, 255, 0.05);

    /* Accents — blue family to match the workbench pill */
    --portco-accent: #93c5fd;
    --portco-accent-hover: #bfdbfe;
    --portco-accent-subtle: rgba(59, 130, 246, 0.14);
  }

  /* Override hardcoded #fafbfc hover backgrounds inside embedded scope.
     ActionCard / Tile / Tr use a literal hex hover that doesn't read from
     tokens — retint via descendant selectors. */
  .portco-embedded button:hover,
  .portco-embedded tr:hover td {
    background: rgba(255, 255, 255, 0.04);
  }

  /* Embedded surface shouldn't claim full viewport height — it lives inside
     a workbench panel that already controls layout. */
  .portco-embedded > div[class*="Surface"],
  .portco-embedded section[class*="Surface"] {
    min-height: auto;
  }
`;

export const Surface = styled.div`
  background: ${portcoTokens.surfaceBody};
  color: ${portcoTokens.inkBody};
  font-family: ${portcoTokens.fontSans};
  min-height: 100vh;
`;

export const Header = styled.header`
  background: ${portcoTokens.surfaceHeader};
  color: ${portcoTokens.inkOnDark};
  padding: 14px 40px;
  border-bottom: 1px solid #000;
`;

export const HeaderInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

export const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${portcoTokens.inkOnDark};
  font-weight: 600;
`;

export const HeaderBrandMark = styled.span`
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 8px;
  overflow: hidden;
  background-color: #020617;
  background-image: url('/assets/axiomflow-logo.png');
  background-repeat: no-repeat;
  background-size: 235% 235%;
  background-position: center 63%;
  filter: brightness(1.28) saturate(1.12);
  font-family: ${portcoTokens.fontMono};
  font-size: 11px;
  text-align: center;
  line-height: 28px;
  letter-spacing: 0;
  color: transparent;
`;

export const HeaderCrumb = styled.div`
  font-size: 12px;
  letter-spacing: 0.04em;
  color: ${portcoTokens.inkOnDarkMuted};
  text-transform: uppercase;
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: 12px;
  color: ${portcoTokens.inkOnDarkMuted};
`;

export const Body = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 48px 40px 80px 40px;
`;

export const BodyNarrow = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 40px 64px 40px;
`;

export const Hero = styled.section`
  padding-bottom: 32px;
  border-bottom: 1px solid ${portcoTokens.hairline};
  margin-bottom: 32px;
`;

export const HeroEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 12px;
`;

export const HeroTitle = styled.h1`
  font-size: 40px;
  line-height: 1.1;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  margin: 0 0 12px 0;
  letter-spacing: -0.01em;
`;

export const HeroSub = styled.p`
  font-size: 16px;
  line-height: 1.55;
  color: ${portcoTokens.inkMuted};
  max-width: 880px;
  margin: 0;
`;

export const StatStrip = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid ${portcoTokens.hairline};
  border-bottom: 1px solid ${portcoTokens.hairline};
  margin-bottom: 48px;
  background: ${portcoTokens.surfaceBody};
`;

export const StatCell = styled.div`
  padding: 24px 28px;
  border-right: 1px solid ${portcoTokens.hairline};

  &:last-child {
    border-right: none;
  }
`;

export const StatLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 8px;
`;

export const StatValue = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 26px;
  font-weight: 500;
  color: ${portcoTokens.inkPrimary};
  letter-spacing: -0.01em;
`;

export const StatSub = styled.div`
  font-size: 11px;
  color: ${portcoTokens.inkSubtle};
  margin-top: 6px;
  letter-spacing: 0.02em;
`;

export const SectionLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 16px;
  font-weight: 600;
`;

export const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  margin: 0 0 24px 0;
  letter-spacing: -0.005em;
`;

export const TwoUp = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 48px;
`;

export const ActionCard = styled.button`
  text-align: left;
  background: ${portcoTokens.surfaceTile};
  border: 1px solid ${portcoTokens.hairline};
  padding: 28px 28px 24px 28px;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: inherit;
  color: inherit;
  width: 100%;

  &:hover {
    border-color: ${portcoTokens.inkPrimary};
    background: #fafbfc;
  }

  &:focus-visible {
    outline: 2px solid ${portcoTokens.accent};
    outline-offset: 2px;
  }
`;

export const ActionCardEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
`;

export const ActionCardTitle = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  letter-spacing: -0.005em;
`;

export const ActionCardBody = styled.p`
  font-size: 14px;
  color: ${portcoTokens.inkMuted};
  margin: 0;
  line-height: 1.55;
`;

export const ActionBullets = styled.ul`
  list-style: none;
  padding: 0;
  margin: 4px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ActionBullet = styled.li`
  font-size: 13px;
  color: ${portcoTokens.inkBody};
  display: flex;
  gap: 10px;
  line-height: 1.5;

  &::before {
    content: '✓';
    color: ${portcoTokens.statusPassing};
    font-weight: 600;
    flex-shrink: 0;
    width: 14px;
  }
`;

export const ActionCardFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid ${portcoTokens.hairline};
  margin-top: 4px;
`;

export const ActionCardCta = styled.span`
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.accent};
  font-weight: 600;
`;

export const BadgeStrip = styled.section`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  border: 1px solid ${portcoTokens.hairline};
  margin-bottom: 48px;
  background: ${portcoTokens.surfaceBody};
`;

export const BadgeTile = styled.div`
  padding: 20px 24px;
  border-right: 1px solid ${portcoTokens.hairline};

  &:last-child {
    border-right: none;
  }
`;

export const BadgeMark = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 11px;
  letter-spacing: 0.08em;
  color: ${portcoTokens.inkSubtle};
  margin-bottom: 8px;
  text-transform: uppercase;
`;

export const BadgeLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  margin-bottom: 4px;
`;

export const BadgeStatus = styled.div<{ $tone?: 'live' | 'beta' | 'idle' }>`
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) =>
    p.$tone === 'live'
      ? portcoTokens.statusPassing
      : p.$tone === 'beta'
      ? portcoTokens.statusWarning
      : portcoTokens.inkSubtle};
  font-weight: 600;
`;

export const TileGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

export const Tile = styled.button`
  text-align: left;
  background: ${portcoTokens.surfaceTile};
  border: 1px solid ${portcoTokens.hairline};
  padding: 20px 22px;
  cursor: pointer;
  transition: border-color 0.12s ease, background 0.12s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: inherit;
  color: inherit;

  &:hover {
    border-color: ${portcoTokens.inkPrimary};
    background: #fafbfc;
  }

  &:focus-visible {
    outline: 2px solid ${portcoTokens.accent};
    outline-offset: 2px;
  }
`;

export const TileTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const TileName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  letter-spacing: -0.005em;
`;

export const TileCusip = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 11px;
  color: ${portcoTokens.inkSubtle};
  letter-spacing: 0.04em;
`;

export const TileMeta = styled.div`
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: ${portcoTokens.inkMuted};
`;

export const TileMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const TileMetricLabel = styled.span`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
`;

export const TileMetricValue = styled.span`
  font-family: ${portcoTokens.fontMono};
  font-size: 13px;
  color: ${portcoTokens.inkPrimary};
`;

export const StatusPill = styled.span<{ $severity: 'BREACHED' | 'WARNING' | 'PASSING' | 'NO_DATA' }>`
  display: inline-block;
  padding: 3px 10px;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
  border: 1px solid;
  border-color: ${(p) =>
    p.$severity === 'BREACHED'
      ? portcoTokens.statusBreached
      : p.$severity === 'WARNING'
      ? portcoTokens.statusWarning
      : p.$severity === 'PASSING'
      ? portcoTokens.statusPassing
      : portcoTokens.hairlineStrong};
  color: ${(p) =>
    p.$severity === 'BREACHED'
      ? portcoTokens.statusBreached
      : p.$severity === 'WARNING'
      ? portcoTokens.statusWarning
      : p.$severity === 'PASSING'
      ? portcoTokens.statusPassing
      : portcoTokens.inkSubtle};
  background: ${(p) =>
    p.$severity === 'BREACHED'
      ? portcoTokens.statusBreachedSubtle
      : p.$severity === 'WARNING'
      ? portcoTokens.statusWarningSubtle
      : p.$severity === 'PASSING'
      ? portcoTokens.statusPassingSubtle
      : portcoTokens.statusNoDataSubtle};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: ${portcoTokens.surfaceBody};
`;

export const Thead = styled.thead`
  background: ${portcoTokens.surfaceMuted};
`;

export const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
  border-bottom: 1px solid ${portcoTokens.hairline};
`;

export const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${portcoTokens.divider};
  color: ${portcoTokens.inkBody};
  vertical-align: top;
`;

export const TdMono = styled(Td)`
  font-family: ${portcoTokens.fontMono};
  font-size: 12.5px;
`;

export const Tr = styled.tr`
  &:hover ${Td} {
    background: #fafbfc;
  }
`;

export const ContextStrip = styled.div`
  background: ${portcoTokens.surfaceMuted};
  border-bottom: 1px solid ${portcoTokens.hairline};
  padding: 20px 40px;
`;

export const ContextStripInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr repeat(5, 1fr);
  gap: 32px;
  align-items: end;
`;

export const ContextName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ContextNameTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  letter-spacing: -0.005em;
`;

export const ContextNameSub = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 12px;
  color: ${portcoTokens.inkSubtle};
  letter-spacing: 0.04em;
`;

export const ContextField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ContextFieldLabel = styled.div`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
`;

export const ContextFieldValue = styled.div`
  font-family: ${portcoTokens.fontMono};
  font-size: 14px;
  color: ${portcoTokens.inkPrimary};
`;

export const TabBar = styled.nav`
  background: ${portcoTokens.surfaceBody};
  border-bottom: 1px solid ${portcoTokens.hairline};
  padding: 0 40px;
`;

export const TabBarInner = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  gap: 0;
`;

export const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 14px 24px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$active ? portcoTokens.inkPrimary : portcoTokens.inkMuted)};
  letter-spacing: 0.02em;
  cursor: pointer;
  border-bottom: 2px solid ${(p) => (p.$active ? portcoTokens.inkPrimary : 'transparent')};
  margin-bottom: -1px;
  font-family: inherit;

  &:hover {
    color: ${portcoTokens.inkPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${portcoTokens.accent};
    outline-offset: -2px;
  }
`;

export const Panel = styled.section`
  background: ${portcoTokens.surfaceBody};
  border: 1px solid ${portcoTokens.hairline};
  margin-bottom: 24px;
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${portcoTokens.hairline};
`;

export const PanelTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${portcoTokens.inkPrimary};
  margin: 0;
  letter-spacing: 0.01em;
`;

export const PanelMeta = styled.div`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkSubtle};
`;

export const PanelBody = styled.div`
  padding: 16px 20px;
`;

export const Empty = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: ${portcoTokens.inkSubtle};
  font-size: 13px;
`;

export const Loading = styled.div`
  padding: 80px 24px;
  text-align: center;
  color: ${portcoTokens.inkSubtle};
  font-size: 13px;
  letter-spacing: 0.04em;
`;
